/**
 * Globe 3D de la barre latérale du cockpit.
 * Un point cyan = un visiteur encore sur le site. Un arc violet part d'Antibes vers chaque visiteur éloigné.
 * Le chiffre est le nombre réel de présences (même liste que « En direct »).
 */
import { memo, useEffect, useRef } from 'react';
import createGlobe from 'cobe';

const SITE = [43.58, 7.13];
const CYAN = [0.25, 0.95, 1];
const ARC = [0.7, 0.32, 0.98];

function nudge(person, index) {
  const lat = Number(person.lat);
  const lon = Number(person.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  const text = String(person.sid || index);
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  const step = person.approx ? 1.1 : 0.12;
  return [lat + ((hash % 5) - 2) * step * 0.35, lon + (((hash >> 3) % 5) - 2) * step];
}

function sceneOf(live, pulse) {
  const placed = (live || []).map(nudge).filter(Boolean);
  const markers = placed.map((location) => ({ location, size: pulse, color: CYAN }));
  const arcs = placed
    .filter(([lat, lon]) => (lat - SITE[0]) ** 2 + (lon - SITE[1]) ** 2 > 16)
    .map((location) => ({ from: SITE, to: location, color: ARC }));
  return { markers, arcs, placed };
}

/** Le canvas est créé ici, pas dans le JSX : cobe le déplace, et un redessin React (chaque seconde) cassait toute la page. */
const GlobeStage = memo(function GlobeStage({ liveRef }) {
  const stageRef = useRef(null);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    const canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    stage.appendChild(canvas);
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    const size = () => Math.max(160, Math.floor(stage.clientWidth || 248));
    let phi = 0.6;
    let dragX = null;
    let dragPhi = 0;
    let focused = false;
    let globe;
    try {
      globe = createGlobe(canvas, {
      devicePixelRatio: pixelRatio,
      width: size(),
      height: size(),
      phi,
      theta: 0.28,
      dark: 1,
      diffuse: 1.35,
      mapSamples: 20000,
      mapBrightness: 12,
      mapBaseBrightness: 0.08,
      baseColor: [0.48, 0.55, 0.72],
      markerColor: CYAN,
      glowColor: [0.18, 0.4, 1],
      arcColor: ARC,
      arcWidth: 0.75,
      arcHeight: 0.34,
      markerElevation: 0.02,
      opacity: 0.96,
      markers: [],
      arcs: [],
    });
    } catch {
      return () => { canvas.remove(); };
    }
    const onDown = (event) => {
      dragX = event.clientX;
      dragPhi = phi;
      canvas.style.cursor = 'grabbing';
    };
    const onMove = (event) => {
      if (dragX === null) return;
      phi = dragPhi + (event.clientX - dragX) / 140;
    };
    const onUp = () => {
      dragX = null;
      canvas.style.cursor = 'grab';
    };
    canvas.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    const observer = new ResizeObserver(() => {
      globe.update({ width: size(), height: size() });
    });
    observer.observe(stage);
    let frame = 0;
    const tick = () => {
      frame = window.requestAnimationFrame(tick);
      if (stage.offsetParent === null) return;
      if (dragX === null) phi += 0.0018;
      const pulse = 0.055 + Math.sin(performance.now() / 320) * 0.014;
      const scene = sceneOf(liveRef.current, pulse);
      if (!focused && scene.placed.length) {
        const lon = scene.placed.reduce((sum, spot) => sum + spot[1], 0) / scene.placed.length;
        phi = -(lon * Math.PI) / 180 - Math.PI / 2;
        focused = true;
      }
      globe.update({ phi, markers: scene.markers, arcs: scene.arcs });
    };
    tick();
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
      canvas.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      globe.destroy();
      stage.replaceChildren();
    };
  }, [liveRef]);

  return <div className="ck-globe-stage" ref={stageRef} />;
});

export default function LiveGlobe({ live, onOpen }) {
  const liveRef = useRef(live);
  liveRef.current = live;
  const count = Array.isArray(live) ? live.length : 0;
  const label = count > 1 ? `${count} visiteurs en direct` : `${count} visiteur en direct`;
  return (
    <div className="ck-globe">
      <GlobeStage liveRef={liveRef} />
      <button type="button" className="ck-globe-count" onClick={onOpen} aria-label={label}>
        <strong>{count}</strong>
        <em><span className="ck-dot is-live" /> en direct</em>
      </button>
    </div>
  );
}
